import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useAtom, useSetAtom, useAtomValue } from 'jotai'
import { useRouter } from 'next/router'
import { isBefore } from 'date-fns'
import { useDispatch } from 'react-redux'
import routesConfig from '@mindfulchefuk/config/routesConfig'
import {
  deliveryDateAtom,
  mealPlanAtom,
  portionCountAtom,
  recipesInBasketAtom,
  selectedSortByAtom,
  voucherCodeAtom,
} from '@mindfulchefuk/store/onboarding'

import { useCMSPageBanner } from '@mindfulchefuk/features/CMS/hooks/useCMSPageBanner'
import { useIsNewCustomer } from '@mindfulchefuk/features/Onboarding/hooks/useIsNewCustomer'
import { useChooseRecipesContextActions } from '@mindfulchefuk/onboarding/context/ChooseRecipesContext/useChooseRecipesContextActions'
import { useGetRecipeAvailabilities } from '@mindfulchefuk/hooks/useGetRecipeAvailabilities'
import { useAvailableDates } from '@mindfulchefuk/features/Operations/hooks/useAvailableDates'

import { TMealPlan } from '@mindfulchefuk/config/mealPlanConfig'
import { PortionCount } from '@mindfulchefuk/config/portionCountConfig'
import { RecipeSortId } from '@mindfulchefuk/config/sortOptionsConfig'
import { CMSBanner } from '@mindfulchefuk/features/CMS/interfaces'
import {
  Recipe,
  RecipeWithQuantity,
  TSortByFilter,
} from '@mindfulchefuk/features/Recipes/interfaces'
import { THandleIncreaseOrDecrement } from '@mindfulchefuk/onboarding/types'
import {
  TRecipeAvailability,
  TSummaryAvailabilityRecipe,
} from '@mindfulchefuk/types/recipeAvailabilities'

import {
  logCheckoutStart,
  logSocialCheckoutStart,
} from '@mindfulchefuk/features/Onboarding/utils/onboardingAnalytics'
import { addNotification } from '@mindfulchefuk/features/Notifications/notificationsSlice'

import { getMinimumMaximumRecipesForPortion } from '@mindfulchefuk/services/getMinimumMaximumRecipesForPortion'
import { transformSummaryAvailabilityToRecipeWithQuantity } from '@mindfulchefuk/onboarding/helpers/dataTransforms/transformRecipeAvailabilitiesToRecipeWithQuantity'
import { sortRecipesBySortBy } from '@mindfulchefuk/features/Recipes/utils/sortRecipesBySortBy'
import getPaymentDate from '@mindfulchefuk/helpers/js/getPaymentDate'

import { postOnboardingPreview } from '@mindfulchefuk/onboarding/services/postOnboardingPreview'

export type TChooseRecipesContext = {
  banner: CMSBanner
  expandedRecipeFilter: TSortByFilter | null
  expectedPaymentDate: Date
  firstAvailableDeliveryDate: Date
  handleAddRecipe: ({
    recipe,
    clickOrigin,
  }: THandleIncreaseOrDecrement<Recipe | RecipeWithQuantity>) => void
  handleConfirmRecipesClick: () => void
  handleRemoveRecipe: ({
    recipe,
    clickOrigin,
  }: THandleIncreaseOrDecrement<Recipe | RecipeWithQuantity>) => void
  handleUpdateDate: (date: Date) => void
  handleUpdateMealPlan: (mealPlan: TMealPlan) => void
  handleUpdatePortionCount: (portionCount: PortionCount) => void
  handleUpdateSortBy: (sortBy: RecipeSortId) => void
  isCartDrawerOpen: boolean
  isFetchingOnboardingPreviewData: boolean
  isLoading: boolean
  lastAvailableDeliveryDate: Date
  maximumRecipesForPortion: number
  mealPlan: TMealPlan
  minimumRecipesForPortion: number
  newestCartItem: Recipe | RecipeWithQuantity | undefined
  onboardingOrderPreviewTotals: {
    total: string
    totalAfterDiscount: string
    shippingPrice: string
  }
  recipes: RecipeWithQuantity[]
  selectedDeliveryDate: Date
  selectedSortBy: RecipeSortId
  selectedPortionCount: PortionCount
  setExpandedRecipeFilter: (filter: TSortByFilter) => void
  toggleCartDrawer: () => void
  toggleRecipeFilter: (filter: TSortByFilter) => void
  totalNumberOfRecipes: number
  unavailableDeliveryDates: string[]
}

const ChooseRecipesContext = React.createContext<
  TChooseRecipesContext | undefined
>(undefined)

export const useChooseRecipesContext = () => {
  const context = useContext(ChooseRecipesContext)

  if (context === undefined) {
    throw new Error(
      'useChooseRecipesContext must be wrapped with ChooseRecipesProvider'
    )
  }

  return context
}

export const ChooseRecipesProvider = ({
  children,
}: {
  children: ReactNode
}): ReactElement => {
  const router = useRouter()
  const dispatch = useDispatch()

  const setVoucherCode = useSetAtom(voucherCodeAtom)
  const [selectedPortionCount, setPortionCount] = useAtom(portionCountAtom)
  const [selectedDeliveryDate, setDeliveryDateAtom] = useAtom(deliveryDateAtom)
  const [selectedMealPlan, setMealPlanAtom] = useAtom(mealPlanAtom)
  const [selectedSortBy] = useAtom(selectedSortByAtom)
  const recipesInBasket = useAtomValue(recipesInBasketAtom)
  const [
    {
      data: onboardingOrderPreview,
      mutate: handlePostOnboardingPreview,
      isPending: onboardingOrderPreviewIsPending,
    },
  ] = useAtom(postOnboardingPreview)

  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState<boolean>(false)
  const [newestCartItem, setNewestCartItem] = useState<
    Recipe | RecipeWithQuantity | undefined
  >(undefined)

  // 'New' means they've not added card details or had a delivery yet,
  // which for us means only Vitality customers at this point
  const { isNewCustomer: isVitalityCustomer } = useIsNewCustomer()

  const toggleCartDrawer = useCallback(
    () => setIsCartDrawerOpen((current) => !current),
    []
  )

  const [expandedRecipeFilter, setExpandedRecipeFilter] =
    useState<TSortByFilter | null>('portion-count')

  const {
    handleAddRecipe,
    handleRemoveRecipe,
    handleUpdateDate,
    handleUpdateMealPlan,
    handleUpdatePortionCount,
    handleUpdateSortBy,
    toggleFilter,
  } = useChooseRecipesContextActions({
    expandedRecipeFilter,
    selectedMealPlan: selectedMealPlan as TMealPlan,
    selectedPortionCount,
    selectedSortBy,
    setExpandedRecipeFilter,
    setNewestCartItem,
  })

  const {
    recipes: {
      firstAvailableDeliveryDate,
      lastAvailableDeliveryDate,
      unavailableDates,
    },
    isLoading: isLoadingAvailableDates,
  } = useAvailableDates()

  const isSelectedDateUnavailable = useMemo(
    () =>
      unavailableDates.includes(selectedDeliveryDate) ||
      isBefore(selectedDeliveryDate, firstAvailableDeliveryDate),
    [selectedDeliveryDate, firstAvailableDeliveryDate, unavailableDates]
  )

  const { minimumRecipes, maximumRecipes } =
    getMinimumMaximumRecipesForPortion(selectedPortionCount)

  const chooseRecipesBanner = useCMSPageBanner({
    page: 'page-recipe-onboarding',
  })

  const {
    data: recipeAvailabilities,
    isLoading: isRecipeAvailabilitiesLoading,
  } = useGetRecipeAvailabilities({
    deliveryDate: isSelectedDateUnavailable
      ? firstAvailableDeliveryDate
      : selectedDeliveryDate,
    portionCount: selectedPortionCount,
    mealPlan: selectedMealPlan as TMealPlan,
  })

  const recipes = useMemo(
    () =>
      recipeAvailabilities?.map(
        (
          recipeAvailability: TRecipeAvailability<TSummaryAvailabilityRecipe>
        ) => {
          return transformSummaryAvailabilityToRecipeWithQuantity({
            recipeAvailability,
            recipesInBasket,
          })
        }
      ),
    [recipeAvailabilities, recipesInBasket]
  )

  const recipesForDisplay = useMemo(
    () => recipes && sortRecipesBySortBy(recipes, selectedSortBy),
    [recipes, selectedSortBy]
  )

  const queriedPortionCount = router.query
    .initialPortionCount as unknown as PortionCount
  const queriedMealPlan = router.query.initialMealPlan as unknown as TMealPlan
  const queriedVoucherCode = router.query.savedVoucherCode as string

  const recipeQuantities = Object.values(recipesInBasket)
  const totalNumberOfRecipes = recipeQuantities.reduce(
    (total, quantity) => total + quantity,
    0
  )

  const expectedPaymentDate = getPaymentDate(selectedDeliveryDate)

  const isLoading =
    !selectedMealPlan ||
    !recipes ||
    isLoadingAvailableDates ||
    isRecipeAvailabilitiesLoading ||
    !selectedDeliveryDate

  const onboardingOrderPreviewTotals = useMemo(
    () => ({
      total: onboardingOrderPreview?.recipePriceBeforeDiscount || '0.00',
      totalAfterDiscount:
        onboardingOrderPreview?.recipePriceWithDiscount || '0.00',
      shippingPrice: onboardingOrderPreview?.shippingPrice || '0.00',
    }),
    [onboardingOrderPreview]
  )

  const handleConfirmRecipesClick = useCallback(() => {
    logCheckoutStart({
      recipeCount: totalNumberOfRecipes,
      price: parseFloat(onboardingOrderPreviewTotals.total),
      totalWithDiscount: parseFloat(
        onboardingOrderPreviewTotals?.totalAfterDiscount
      ),
    })
    logSocialCheckoutStart()

    if (isVitalityCustomer) {
      return router.push(routesConfig.postLoginOnboardingShipping.pathname)
    }
    router.push(routesConfig.aboutYou.pathname)
  }, [
    isVitalityCustomer,
    onboardingOrderPreviewTotals,
    router,
    totalNumberOfRecipes,
  ])

  useEffect(() => {
    if (isSelectedDateUnavailable) {
      setDeliveryDateAtom(firstAvailableDeliveryDate)
      dispatch(addNotification({ id: 'error-date-reset' }))
    }
    // exclude dispatch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectedDateUnavailable])

  useEffect(() => {
    if (!selectedDeliveryDate && !isLoadingAvailableDates) {
      setDeliveryDateAtom(firstAvailableDeliveryDate)
    }
  }, [
    selectedDeliveryDate,
    setDeliveryDateAtom,
    isLoadingAvailableDates,
    firstAvailableDeliveryDate,
  ])

  useEffect(() => {
    if (queriedPortionCount) {
      setPortionCount(queriedPortionCount)
    }
  }, [queriedPortionCount, setPortionCount])

  useEffect(() => {
    if (queriedMealPlan) {
      setMealPlanAtom(queriedMealPlan)
    }
  }, [queriedMealPlan, setMealPlanAtom])

  useEffect(() => {
    if (queriedVoucherCode) {
      setVoucherCode(queriedVoucherCode)
    }
  }, [queriedVoucherCode, setVoucherCode])

  useEffect(() => {
    if (Object.keys(recipesInBasket).length) {
      handlePostOnboardingPreview()
    }
  }, [recipesInBasket, handlePostOnboardingPreview])

  const contextValue = useMemo(
    () => ({
      banner: chooseRecipesBanner ?? null,
      expandedRecipeFilter,
      expectedPaymentDate,
      firstAvailableDeliveryDate: new Date(firstAvailableDeliveryDate),
      handleAddRecipe,
      handleConfirmRecipesClick,
      handleUpdateDate,
      handleUpdateMealPlan,
      handleUpdatePortionCount,
      handleRemoveRecipe,
      handleUpdateSortBy,
      isCartDrawerOpen,
      isFetchingOnboardingPreviewData: onboardingOrderPreviewIsPending,
      isLoading,
      lastAvailableDeliveryDate: new Date(lastAvailableDeliveryDate),
      maximumRecipesForPortion: maximumRecipes,
      mealPlan: selectedMealPlan as TMealPlan,
      minimumRecipesForPortion: minimumRecipes,
      newestCartItem,
      onboardingOrderPreviewTotals,
      recipes: recipesForDisplay,
      selectedDeliveryDate: new Date(selectedDeliveryDate),
      selectedPortionCount,
      selectedSortBy,
      setExpandedRecipeFilter,
      toggleCartDrawer,
      toggleRecipeFilter: toggleFilter,
      totalNumberOfRecipes,
      unavailableDeliveryDates: unavailableDates,
    }),
    [
      chooseRecipesBanner,
      expandedRecipeFilter,
      expectedPaymentDate,
      firstAvailableDeliveryDate,
      handleAddRecipe,
      handleConfirmRecipesClick,
      handleRemoveRecipe,
      handleUpdateDate,
      handleUpdateMealPlan,
      handleUpdatePortionCount,
      handleUpdateSortBy,
      isCartDrawerOpen,
      isLoading,
      lastAvailableDeliveryDate,
      maximumRecipes,
      minimumRecipes,
      newestCartItem,
      onboardingOrderPreviewTotals,
      onboardingOrderPreviewIsPending,
      recipesForDisplay,
      selectedDeliveryDate,
      selectedMealPlan,
      selectedPortionCount,
      selectedSortBy,
      toggleCartDrawer,
      toggleFilter,
      totalNumberOfRecipes,
      unavailableDates,
    ]
  )

  return (
    <ChooseRecipesContext.Provider value={contextValue}>
      {children}
    </ChooseRecipesContext.Provider>
  )
}
